import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  ActionIcon,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import IconCalendar from "@tabler/icons-react/dist/esm/icons/IconCalendar.mjs";
import IconEqual from "@tabler/icons-react/dist/esm/icons/IconEqual.mjs";
import IconMathGreater from "@tabler/icons-react/dist/esm/icons/IconMathGreater.mjs";
import IconMathLower from "@tabler/icons-react/dist/esm/icons/IconMathLower.mjs";
import IconSpacingHorizontal from "@tabler/icons-react/dist/esm/icons/IconSpacingHorizontal.mjs";
import IconX from "@tabler/icons-react/dist/esm/icons/IconX.mjs";
import classes from "./dateSearchFilter.module.scss";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { dateFormat } from "../../../utils/dateFormat";
dayjs.extend(utc);

// ─── Option definitions ───────────────────────────────────────────────────────

const ALL_OPTIONS = [
  {
    id: "equals",
    label: "Exact",
    icon: <IconEqual size={13} />,
    fields: "single",
  },
  {
    id: "lessThan",
    label: "Before",
    icon: <IconMathLower size={13} />,
    fields: "single",
  },
  {
    id: "greaterThan",
    label: "After",
    icon: <IconMathGreater size={13} />,
    fields: "single",
  },
  {
    id: "between",
    label: "Between",
    icon: <IconSpacingHorizontal size={13} />,
    fields: "both",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toNativeDate(date) {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === "object" && typeof date.toDate === "function")
    return date.toDate();
  const d = new Date(date);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(date) {
  const d = toNativeDate(date);
  if (!d) return "";
  return dayjs(date).format(dateFormat);
}

function buildApiValues(mode, single, from, to) {
  // first we format in the way API expects the values

  const fromStartOfTheDay = (date) =>
    dayjs(date).utcOffset(0, true).startOf("day").toISOString();
  const fromEndOfTheDay = (date) =>
    dayjs(date).utcOffset(0, true).add(1, "day").startOf("day").toISOString();

  if (!mode) {
    return { exactDate: null, startDate: null, endDate: null };
  }

  if (mode === "equals") {
    return {
      exactDate: fromStartOfTheDay(single),
      startDate: null,
      endDate: null,
    };
  }

  if (mode === "lessThan") {
    return {
      exactDate: null,
      startDate: null,
      endDate: fromStartOfTheDay(single),
    };
  }

  if (mode === "greaterThan") {
    return {
      exactDate: null,
      startDate: fromStartOfTheDay(single),
      endDate: null,
    };
  }

  if (mode === "between") {
    return {
      exactDate: null,
      startDate: fromStartOfTheDay(from),
      endDate: fromEndOfTheDay(to),
    };
  }

  return { exactDate: null, startDate: null, endDate: null };
}

function triggerLabel(mode, single, from, to) {
  const opt = ALL_OPTIONS.find((o) => o.id === mode);
  const label = opt?.label ?? mode;
  if (mode === "between")
    return from && to ? `${formatDate(from)} - ${formatDate(to)}` : label;
  return single ? `${label}: ${formatDate(single)}` : label;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * DateSearchFilter — Mantine v8
 *
 * Props:
 *  onChange(apiValues)        { startDate, endDate } — called on every change
 *  enabledOptions  string[]   subset of ["equals","lessThan","greaterThan","between"]
 *  placeholder  string        trigger button placeholder text
 *  defaultMode  string        pre-selected mode id
 */
export default function DateSearchFilter({
  onChange,
  enabledOptions,
  placeholder = "Select date",
  defaultMode = null,
}) {
  const options = enabledOptions
    ? ALL_OPTIONS.filter((o) => enabledOptions.includes(o.id))
    : ALL_OPTIONS;

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(defaultMode);
  const [single, setSingle] = useState(null);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  const wrapperRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target)) return;
      if (e.target.closest("[data-portal]")) return;
      if (e.target.closest(".mantine-Popover-dropdown")) return;
      if (e.target.closest(".mantine-DatePicker-root")) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function selectMode(id) {
    setMode(id);
    setSingle(null);
    setFrom(null);
    setTo(null);
  }

  function reset() {
    setMode(null);
    setSingle(null);
    setFrom(null);
    setTo(null);
    setOpen(false);
    onChange?.({ exactDate: null, startDate: null, endDate: null });
  }

  // ── Dropdown mode ─────────────────────────────────────────────────────────
  return (
    <Box ref={wrapperRef} className={classes["dropdown-mode-wrapper"]}>
      {/* Trigger button */}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => e.key === "Enter" && setOpen((v) => !v)}
        className={classes["trigger-button"]}
      >
        {mode?.icon}
        <Text
          size="sm"
          className={classes["trigger-button-text"]}
          style={{
            flex: 1,
            color: mode
              ? "var(--mantine-color-text)"
              : "var(--mantine-color-placeholder)",
          }}
        >
          {mode ? triggerLabel(mode, single, from, to) : placeholder}
        </Text>

        {mode && (
          <ActionIcon
            size="xs"
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation();
              reset();
            }}
            aria-label="Clear"
          >
            <IconX size={12} />
          </ActionIcon>
        )}
      </Box>

      {/* Dropdown panel */}
      {open && (
        <Paper
          shadow="md"
          radius="sm"
          onMouseDown={(e) => e.stopPropagation()}
          className={classes["menu-box"]}
        >
          {/* Option list */}
          <Stack gap={2} p={6}>
            {options.map((opt) => {
              const active = mode === opt.id;
              return (
                <Box
                  key={opt.id}
                  onClick={() => selectMode(opt.id)}
                  className={classes["menu-options"]}
                  style={{
                    background: active
                      ? "var(--mantine-color-green-light)"
                      : undefined,
                    color: active
                      ? "var(--mantine-color-green-light-color)"
                      : undefined,
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  <Text className={classes["menu-option"]}>{opt.icon}</Text>
                  {opt.label}
                </Box>
              );
            })}
          </Stack>

          {/* Date input section — shown only after a mode is selected */}
          {mode && (
            <>
              <Divider />
              <Box p="sm">
                {mode === "between" ? (
                  <Stack gap="xs">
                    <DateInput
                      label="From"
                      value={from}
                      onChange={(d) => setFrom(toNativeDate(d))}
                      placeholder="Pick start date"
                      leftSection={<IconCalendar size={16} />}
                      maxDate={to ?? undefined}
                      clearable
                      size="sm"
                    />
                    <DateInput
                      label="To"
                      value={to}
                      onChange={(d) => setTo(toNativeDate(d))}
                      placeholder="Pick end date"
                      leftSection={<IconCalendar size={16} />}
                      minDate={from ?? undefined}
                      clearable
                      size="sm"
                    />
                  </Stack>
                ) : (
                  <DateInput
                    value={single}
                    onChange={(d) => setSingle(toNativeDate(d))}
                    placeholder="Pick a date"
                    leftSection={<IconCalendar size={16} />}
                    clearable
                    size="sm"
                  />
                )}

                <Group justify="flex-end" mt="sm">
                  <Button
                    size="xs"
                    onClick={() => {
                      onChange?.(buildApiValues(mode, single, from, to));
                      setOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                </Group>
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
